import {DecorativeGrid} from './DecorativeGrid';
import {cn} from '../../lib/utils';
import {Typography} from '../Typography';

import type {ReactNode} from 'react';

const regularSizeConfig = {
  content: 'gap-8',
  group: 'gap-5',
  text: 'gap-2 max-w-88',
  title: 'lg',
  subtitle: 'sm'
} as const;

const emptyStateSizesConfig = {
  default: {...regularSizeConfig, root: 'py-16'},
  compact: {...regularSizeConfig, root: 'py-10'},
  sm: {
    root: 'py-6',
    content: 'gap-4',
    group: 'gap-3',
    text: 'gap-1.5 max-w-64',
    title: 'sm',
    subtitle: 'xs'
  }
} as const;

export interface EmptyStateProps {
  illustration?: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  /** `default` (py-16) for full-page empty states; `compact` (py-10) for embedding inside a table or card; `sm` also scales the text and spacing down for small panes. */
  size?: keyof typeof emptyStateSizesConfig;
  /** Set to false to drop the decorative background grid. */
  showGrid?: boolean;
  className?: string;
}

export function EmptyState({
  illustration,
  title,
  subtitle,
  action,
  size = 'default',
  showGrid = true,
  className
}: EmptyStateProps) {
  const sizes = emptyStateSizesConfig[size];

  return (
    <div
      data-slot="empty-state"
      className={cn('relative flex flex-col items-center overflow-hidden', sizes.root, className)}
    >
      {showGrid && <DecorativeGrid />}

      {/* Content */}
      <div className={cn('relative z-10 flex flex-col items-center', sizes.content)}>
        {/* Illustration + text group */}
        <div className={cn('flex flex-col items-center', sizes.group)}>
          {illustration && <div data-slot="empty-state-illustration">{illustration}</div>}

          {/* Text group */}
          <div className={cn('flex flex-col items-center text-center', sizes.text)}>
            <Typography as="h3" size={sizes.title} weight="semibold" className="whitespace-normal">
              {title}
            </Typography>
            {subtitle && (
              <Typography
                size={sizes.subtitle}
                color="tertiary"
                className="whitespace-normal text-balance"
              >
                {subtitle}
              </Typography>
            )}
          </div>
        </div>

        {action && <div data-slot="empty-state-action">{action}</div>}
      </div>
    </div>
  );
}
